import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Pencil, Trash2, AlertCircle, Eye, ArrowRight, Database, Rss, Network, List, ChevronUp, ChevronDown, FileSpreadsheet, FileCode, Braces, History } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { cn } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { StatusBadge, fmtDate, fmtNum, fmtDur } from '../components/feeds/shared';
import usePollWhileActive from '../hooks/usePollWhileActive';
import { api } from '../api/feedy';

const TYPES = [
  { value: 'shopify', label: 'Shopify' },
  { value: 'postgres', label: 'PostgreSQL table / query' },
  { value: 'csv', label: 'CSV file' },
  { value: 'xml', label: 'XML feed' },
  { value: 'json', label: 'JSON feed' },
  { value: 'google_sheet', label: 'Google Sheet (CSV export)' },
];

const blank = {
  name: '', type: 'csv', role: 'base', locale: '', enabled: true, secret_ref: '',
  config: { url: '', id_field: 'id', delimiter: ',', shop_domain: '', api_version: '2024-10', keep_columns: [] },
};

function ShopifyLogo({ className }) {
  return (
    <svg viewBox="0 0 448 512" className={className} fill="#95BF47" aria-hidden="true">
      <path d="M388.3 104.1c-.3-2.5-2.5-3.9-4.3-4-1.8-.2-37.8-2.8-37.8-2.8s-25-24.8-27.7-27.6c-2.8-2.8-8.2-1.9-10.3-1.3-.3.1-5.4 1.7-13.8 4.3-8.2-23.6-22.7-45.3-48.2-45.3h-2.2C236.6 8.2 227.5 0 219.6 0c-61.2 0-90.5 76.5-99.7 115.4-23.8 7.4-40.7 12.6-42.9 13.3-13.3 4.2-13.7 4.6-15.4 17.1C60.3 155.4 25.4 425.9 25.4 425.9L296.7 512l171.5-37C468.2 475 388.6 106.6 388.3 104.1zM263.3 73.6c-6.6 2-14.1 4.4-22.2 6.9 0-1.6 0-3.1 0-4.8 0-14.4-2-26-5.2-35.2 12.9 1.6 21.5 16.3 27.4 33.1zM219.2 43.9c3.6 9 5.9 21.9 5.9 39.4v2.5c-14.4 4.5-30.1 9.3-45.8 14.2 8.8-34 25.3-50.4 39.9-56.1zM201.5 27c2.6 0 5.2.9 7.7 2.6-19.2 9-39.7 31.8-48.4 77.3-12.6 3.9-24.9 7.7-36.2 11.2C134.8 81.1 159.7 27 201.5 27z"/>
    </svg>
  );
}

function SourceIcon({ type }) {
  const cls = 'h-4 w-4 shrink-0';
  if (type === 'shopify') return <ShopifyLogo className={cls} />;
  if (type === 'postgres') return <Database className={`${cls} text-sky-600`} />;
  if (type === 'csv' || type === 'tsv') return <FileSpreadsheet className={`${cls} text-emerald-600`} />;
  if (type === 'xml') return <FileCode className={`${cls} text-amber-600`} />;
  if (type === 'json') return <Braces className={`${cls} text-violet-600`} />;
  return <Database className={`${cls} text-muted-foreground`} />;
}

export default function Sources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [history, setHistory] = useState(null); // source whose run history is open
  const [view, setView] = useState('table');
  const [feeds, setFeeds] = useState([]);
  const [fieldCount, setFieldCount] = useState(null);
  const [liveCounts, setLiveCounts] = useState({});
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // `soft` refetches without flipping the page spinner — used by the live poll
  // so a syncing source flips to success/error in place, no flicker.
  const load = (soft = false) => {
    if (!soft) setLoading(true);
    return api.sources.list().then(setSources).finally(() => { if (!soft) setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  // While any source is syncing, poll so its status updates on its own.
  const anyRunning = sources.some((s) => s.last_status === 'running');
  usePollWhileActive(anyRunning, () => load(true));
  useEffect(() => {
    api.feeds.list().then(setFeeds).catch(() => {});
    api.items.fields().then((f) => setFieldCount(f.length)).catch(() => {});
  }, []);

  // Live-poll row counts for Postgres sources (live tables — always current).
  useEffect(() => {
    const pg = sources.filter((s) => s.type === 'postgres');
    if (!pg.length) return undefined;
    let active = true;
    const tick = () => pg.forEach((s) =>
      api.sources.liveCount(s.id).then((r) => { if (active) setLiveCounts((c) => ({ ...c, [s.id]: r })); }).catch(() => {}));
    tick();
    const timer = setInterval(tick, 30000);
    return () => { active = false; clearInterval(timer); };
  }, [sources]);

  // rows = raw count(*) of the source; items = unique shopify ids (one Item each)
  const liveOrStored = (s) => {
    const items = s.item_count ?? 0;
    if (s.type === 'postgres') {
      const lc = liveCounts[s.id];
      if (lc?.ok) return { rows: lc.count, items, live: true };
      if (lc && !lc.ok) return { rows: s.row_count || null, items, err: lc.error };
      return { rows: s.row_count || null, items, loading: !s.row_count };
    }
    return { rows: s.row_count || items, items };
  };

  const openNew = () => setEditing({ ...blank, order: sources.length, config: { ...blank.config } });

  useEffect(() => {
    if (params.get('new') === '1') { openNew(); setParams({}, { replace: true }); }
  }, [params, setParams]);
  const openEdit = async (s) => {
    const cfg = { ...blank.config, ...s.config };
    setEditing({ ...s, config: cfg });
    // For Shopify, load the effective Admin GraphQL query so it's visible/editable.
    if (s.type === 'shopify' && !cfg.query) {
      try {
        const r = await api.sources.query(s.id);
        setEditing((e) => (e && e.id === s.id ? { ...e, config: { ...e.config, query: r.query || '' }, _defaultQuery: r.default || '' } : e));
      } catch { /* leave blank */ }
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editing.name, type: editing.type, role: editing.role, order: editing.order ?? 0,
        locale: editing.locale, enabled: editing.enabled, secret_ref: editing.secret_ref, config: editing.config,
      };
      if (editing.id) await api.sources.update(editing.id, payload);
      else await api.sources.create(payload);
      setEditing(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const sync = async (s) => {
    setSyncingId(s.id);
    try { await api.sources.sync(s.id); } catch (_) { /* surfaced via status */ }
    setSyncingId(null);
    load();
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete source "${s.name}" and its items?`)) return;
    await api.sources.remove(s.id);
    load();
  };

  // Reorder the data-prep pipeline (renumbers all sources by position).
  const moveOrder = async (s, dir) => {
    const i = sources.findIndex((x) => x.id === s.id);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= sources.length) return;
    const ids = sources.map((x) => x.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await Promise.all(ids.map((id, idx) => api.sources.update(id, { order: idx })));
    load();
  };

  const isShopify = editing?.type === 'shopify';
  const isPostgres = editing?.type === 'postgres';

  return (
    <AppLayout
      title="Sources"
      description="Where your product data comes from"
      actions={
        <>
          {anyRunning && (
            <span className="mr-1 inline-flex items-center gap-1.5 font-mono text-[11px] text-amber-600" title="A source is syncing — updating automatically">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> live
            </span>
          )}
          <div className="flex rounded-md border border-border p-0.5">
            <button onClick={() => setView('table')} className={cn('flex items-center gap-1.5 rounded px-2.5 py-1 text-[13px] font-medium', view === 'table' ? 'bg-muted text-foreground' : 'text-muted-foreground')}>
              <List className="h-4 w-4" /> Table
            </button>
            <button onClick={() => setView('diagram')} className={cn('flex items-center gap-1.5 rounded px-2.5 py-1 text-[13px] font-medium', view === 'diagram' ? 'bg-muted text-foreground' : 'text-muted-foreground')}>
              <Network className="h-4 w-4" /> Diagram
            </button>
          </div>
          <Button className="gap-2" onClick={openNew}><Plus className="h-4 w-4" /> Add source</Button>
        </>
      }
    >
      {view === 'diagram' && (
        <SourcesDiagram sources={sources} feeds={feeds} fieldCount={fieldCount} liveCounts={liveCounts} onNodeClick={setPreviewing} onFeeds={() => navigate('/feeds')} />
      )}
      {view === 'table' && (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Locale</TableHead>
              <TableHead className="text-right" title="raw rows (count *) / unique items (shopify id)">Rows / Items</TableHead>
              <TableHead>Last sync</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!loading && sources.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No sources yet. Add your 3 Shopify stores and your external CSV.</TableCell></TableRow>
            )}
            {sources.map((s, i) => (
              <TableRow key={s.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-muted-foreground">{String(i + 1).padStart(2, '0')}</span>
                    <SourceIcon type={s.type} />
                    <span className="font-medium">{s.name}</span>
                  </div>
                  {s.last_status === 'error' && s.last_message && (
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-destructive">
                      <AlertCircle className="h-3 w-3" /> {s.last_message.slice(0, 80)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={s.role === 'enrichment' ? 'accent' : 'secondary'} title={s.type_display}>{s.role}</Badge>
                </TableCell>
                <TableCell>{s.locale || '—'}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {(() => {
                    const c = liveOrStored(s);
                    const fmt = (x) => (x == null ? '—' : x.toLocaleString());
                    const same = c.rows != null && c.rows === c.items;
                    return (
                      <span className="inline-flex items-center justify-end gap-1.5"
                        title={c.err || `${fmt(c.rows)} rows (count *) · ${fmt(c.items)} unique items (shopify id)`}>
                        {c.live && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" title="live count(*)" />}
                        {c.loading ? (
                          <span className="text-muted-foreground">…</span>
                        ) : same ? (
                          <span>{fmt(c.items)}</span>
                        ) : (
                          <span>
                            <span className={c.rows == null ? 'text-muted-foreground' : ''}>{fmt(c.rows)}</span>
                            <span className="text-muted-foreground"> / </span>
                            <span>{fmt(c.items)}</span>
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(s.last_synced_at) || 'never'}</TableCell>
                <TableCell><StatusBadge status={s.last_status} /></TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <div className="mr-1 flex flex-col">
                      <button onClick={() => moveOrder(s, 'up')} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30" aria-label="Move up"><ChevronUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => moveOrder(s, 'down')} disabled={i === sources.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30" aria-label="Move down"><ChevronDown className="h-3.5 w-3.5" /></button>
                    </div>
                    <Button variant="ghost" size="sm" className="gap-1" disabled={syncingId === s.id} onClick={() => sync(s)}>
                      <RefreshCw className={`h-4 w-4 ${syncingId === s.id ? 'animate-spin' : ''}`} /> Sync
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setPreviewing(s)} aria-label="Preview data"><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setHistory(s)} aria-label="Sync history" title="Sync history"><History className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(s)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(s)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)}>
        {editing && (
          <>
            <DialogHeader>
              <DialogTitle>{editing.id ? 'Edit source' : 'New source'}</DialogTitle>
              <DialogDescription>Connect a Shopify store or a remote file.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Shopify NL" />
                </div>
                <div className="space-y-1.5">
                  <Label>Locale</Label>
                  <Input value={editing.locale} onChange={(e) => setEditing({ ...editing, locale: e.target.value })} placeholder="NL" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
                    {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                    <option value="base">Base catalog</option>
                    <option value="enrichment">Enrichment (join by ID)</option>
                  </Select>
                </div>
              </div>
              {editing.role === 'enrichment' && (
                <p className="-mt-2 rounded-md bg-primary/10 px-3 py-2 text-xs text-muted-foreground">
                  Enrichment sources don't add products — they merge their columns onto base items that share the same <strong>ID field</strong> value (the join key, e.g. the Shopify variant id).
                </p>
              )}

              {isShopify ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Shop domain</Label>
                    <Input value={editing.config.shop_domain} onChange={(e) => setEditing({ ...editing, config: { ...editing.config, shop_domain: e.target.value } })} placeholder="store.myshopify.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>API version</Label>
                    <Input value={editing.config.api_version} onChange={(e) => setEditing({ ...editing, config: { ...editing.config, api_version: e.target.value } })} />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Token env var (secret_ref)</Label>
                    <Input value={editing.secret_ref} onChange={(e) => setEditing({ ...editing, secret_ref: e.target.value })} placeholder="SHOPIFY_NL_TOKEN" />
                    <p className="text-xs text-muted-foreground">Name of the environment variable holding the Admin API token. The token itself is never stored in the database.</p>
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>Admin GraphQL query — fields retrieved</Label>
                      {editing._defaultQuery && (editing.config.query || '') !== editing._defaultQuery && (
                        <button type="button" onClick={() => setEditing({ ...editing, config: { ...editing.config, query: editing._defaultQuery } })} className="text-xs text-primary hover:underline">Reset to default</button>
                      )}
                    </div>
                    <textarea
                      value={editing.config.query || ''}
                      onChange={(e) => setEditing({ ...editing, config: { ...editing.config, query: e.target.value } })}
                      rows={16}
                      spellCheck={false}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-[11px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="query Products($cursor: String) { products(first: 50, after: $cursor) { nodes { … } } }"
                    />
                    <p className="text-xs text-muted-foreground">The exact Admin GraphQL query that defines which product / variant / image / collection / metafield / French-translation fields are pulled. Edit to add or remove fields; clear to fall back to the built-in default. A broken query fails the next sync.</p>
                  </div>
                </div>
              ) : isPostgres ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <Label>Connection env var (secret_ref)</Label>
                    <Input value={editing.secret_ref} onChange={(e) => setEditing({ ...editing, secret_ref: e.target.value })} placeholder="PIM_DATABASE_URL" className="font-mono text-[13px]" />
                    <p className="text-xs text-muted-foreground">Env var holding the connection string (<span className="font-mono">postgresql://…</span>). The DSN is never stored in the database.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Table</Label>
                    <Input value={editing.config.table || ''} onChange={(e) => setEditing({ ...editing, config: { ...editing.config, table: e.target.value } })} placeholder="pim_product_master" className="font-mono text-[13px]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>ID field (join key)</Label>
                    <Input value={editing.config.id_field} onChange={(e) => setEditing({ ...editing, config: { ...editing.config, id_field: e.target.value } })} placeholder="shopify_variant_id" className="font-mono text-[13px]" />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>WHERE <span className="text-muted-foreground">(optional)</span></Label>
                    <Input value={editing.config.where || ''} onChange={(e) => setEditing({ ...editing, config: { ...editing.config, where: e.target.value } })} placeholder="status = 'active'" className="font-mono text-[13px]" />
                    <p className="text-xs text-muted-foreground">Or use a full query in advanced config. Streams large tables via a server-side cursor.</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <Label>File URL</Label>
                    <Input value={editing.config.url} onChange={(e) => setEditing({ ...editing, config: { ...editing.config, url: e.target.value } })} placeholder="https://storage.example/products.csv" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{editing.role === 'enrichment' ? 'ID field (join key)' : 'ID field'}</Label>
                    <Input value={editing.config.id_field} onChange={(e) => setEditing({ ...editing, config: { ...editing.config, id_field: e.target.value } })} placeholder="id / sku / variant_id" className="font-mono text-[13px]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Delimiter (CSV)</Label>
                    <Input value={editing.config.delimiter} onChange={(e) => setEditing({ ...editing, config: { ...editing.config, delimiter: e.target.value } })} placeholder="," />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Keep columns <span className="text-muted-foreground">(optional — comma separated)</span></Label>
                    <Input
                      value={(editing.config.keep_columns || []).join(', ')}
                      onChange={(e) => setEditing({ ...editing, config: { ...editing.config, keep_columns: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) } })}
                      placeholder="price_BE, compareAt_BE, parent_id"
                      className="font-mono text-[13px]"
                    />
                    <p className="text-xs text-muted-foreground">Limit ingest to these columns (keeps wide/huge files lean). Leave empty for all.</p>
                  </div>
                </div>
              )}

              <label className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <span className="text-sm font-medium">Enabled</span>
                <Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} />
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving || !editing.name}>{saving ? 'Saving…' : 'Save source'}</Button>
            </DialogFooter>
          </>
        )}
      </Dialog>

      <SourcePreviewDialog source={previewing} onClose={() => setPreviewing(null)} />
      <SourceRunsDialog source={history} onClose={() => setHistory(null)} />
    </AppLayout>
  );
}

function SourceRunsDialog({ source, onClose }) {
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!source) { setRuns(null); return; }
    setRuns(null); setLoading(true);
    api.sources.runs(source.id).then(setRuns).catch(() => setRuns([])).finally(() => setLoading(false));
  }, [source]);

  return (
    <Dialog open={!!source} onClose={onClose} className="max-w-3xl">
      {source && (
        <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4" /> {source.name} — sync history
            </DialogTitle>
            <DialogDescription>
              {runs ? `${runs.length} recent run${runs.length === 1 ? '' : 's'}` : 'Loading…'}
            </DialogDescription>
          </DialogHeader>

          {loading && <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>}

          {runs && !loading && runs.length === 0 && (
            <p className="py-12 text-center text-sm text-muted-foreground">No syncs recorded yet — sync this source to start its history.</p>
          )}

          {runs && runs.length > 0 && (
            <div className="max-h-[60vh] overflow-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead className="text-right">Duration</TableHead>
                    <TableHead className="text-right" title="raw rows / unique items">Rows / Items</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(r.started_at) || '—'}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{r.duration_s != null ? fmtDur(r.duration_s) : '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        <span className={r.row_count == null ? 'text-muted-foreground' : ''}>{r.row_count == null ? '—' : fmtNum(r.row_count)}</span>
                        <span className="text-muted-foreground"> / </span>
                        <span className={r.item_count == null ? 'text-muted-foreground' : ''}>{r.item_count == null ? '—' : fmtNum(r.item_count)}</span>
                      </TableCell>
                      <TableCell className="max-w-[320px]">
                        <span className={cn('block truncate text-[12px]', r.status === 'error' ? 'text-destructive' : 'text-muted-foreground')} title={r.message}>
                          {r.message || '—'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}

function SourcePreviewDialog({ source, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('fields');
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!source) { setData(null); return; }
    setData(null); setLoading(true); setTab('fields'); setQ('');
    api.sources.preview(source.id, { sample: 2000, limit: 25 }).then(setData).finally(() => setLoading(false));
  }, [source]);

  return (
    <Dialog open={!!source} onClose={onClose} className="max-w-4xl">
      {source && (
        <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {source.name}
              {source.role === 'enrichment' && <Badge variant="accent">enrichment</Badge>}
            </DialogTitle>
            <DialogDescription>
              {data
                ? `${data.item_count.toLocaleString()} items · ${data.fields.length} fields · stats from ${data.sampled.toLocaleString()} sampled`
                : 'Loading…'}
            </DialogDescription>
          </DialogHeader>

          <div className="mb-3 rounded-md border border-border bg-muted/30 p-3 text-[12px] leading-relaxed">
            <p className="mb-0.5 font-medium">How it's retrieved</p>
            {source.type === 'shopify' ? (
              <p className="text-muted-foreground">
                Shopify Admin <span className="font-mono">GraphQL</span> · API <span className="font-mono">{source.config?.api_version || 'default'}</span> · <span className="font-mono">{source.config?.shop_domain}</span>.
                One row per <b>variant</b>, flattened with its product. Pulls: core product fields (title, description, vendor, productType + its <b>French translation</b> → <span className="font-mono">product_type</span>, tags, link),
                {' '}<b>featuredImage + images</b> (first 20), all <b>collections</b> (joined), <b>every metafield</b> as <span className="font-mono">meta_&lt;ns&gt;_&lt;key&gt;</span>,
                {' '}and per-variant <b>sku, gtin, price, compareAtPrice, availability, inventoryQuantity, inventoryPolicy</b> + selected options.
                {' '}<span className="text-foreground">Every retrieved field is listed &amp; searchable below — grouped by Metafields / Images / Collections / Stock.</span>
              </p>
            ) : source.type === 'postgres' ? (
              <p className="text-muted-foreground">Postgres query · join key <span className="font-mono">{source.config?.id_field}</span>. Columns become internal fields.</p>
            ) : (
              <p className="text-muted-foreground">Remote <span className="font-mono">{(source.type || '').toUpperCase()}</span> file · <span className="font-mono">{source.config?.url}</span> · join key <span className="font-mono">{source.config?.id_field}</span>.</p>
            )}
          </div>

          {loading && <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>}

          {data && (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="fields">Fields ({data.fields.length})</TabsTrigger>
                <TabsTrigger value="data">Data ({data.rows.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="fields">
                <div className="mb-2 flex items-center gap-2">
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fields — e.g. meta, color, stock…" className="h-8 max-w-xs" />
                  <span className="text-[11px] text-muted-foreground">
                    {q ? `${groupFields(data.fields, q).reduce((a, g) => a + g.fields.length, 0)} of ${data.fields.length}` : data.fields.length} fields
                  </span>
                </div>
                <div className="max-h-[52vh] overflow-auto rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Field</TableHead>
                        <TableHead className="w-40">Fill rate</TableHead>
                        <TableHead className="text-right">Distinct</TableHead>
                        <TableHead>Examples</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupFields(data.fields, q).map((grp) => (
                        <React.Fragment key={grp.label}>
                          <TableRow className="bg-muted/50 hover:bg-muted/50">
                            <TableCell colSpan={4} className="py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{grp.label} · {grp.fields.length}</TableCell>
                          </TableRow>
                          {grp.fields.map((f) => (
                            <TableRow key={f.name}>
                              <TableCell className="font-mono text-[12px]">{f.name}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                                    <div className="h-full rounded-full bg-primary" style={{ width: `${f.fill}%` }} />
                                  </div>
                                  <span className="tnum text-[11px] text-muted-foreground">{f.fill}%</span>
                                </div>
                              </TableCell>
                              <TableCell className="tnum text-right text-muted-foreground">{f.distinct}{f.distinct_capped ? '+' : ''}</TableCell>
                              <TableCell className="max-w-[280px] truncate font-mono text-[11px] text-muted-foreground">{f.examples.join('  ·  ')}</TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                      {groupFields(data.fields, q).length === 0 && (
                        <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">No fields match “{q}”.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="data">
                <p className="mb-2 font-mono text-[11px] text-muted-foreground">{data.columns.length} columns · scroll ↔ and ↕</p>
                {/* 2-D scroll: w-max makes the table wider than the box so it scrolls horizontally; sticky header for vertical scroll */}
                <div className="max-h-[55vh] overflow-auto rounded-lg border border-border">
                  <table className="w-max border-collapse text-sm">
                    <thead className="sticky top-0 z-10 bg-card">
                      <tr className="border-b border-border">
                        {data.columns.map((c) => (
                          <th key={c} className="whitespace-nowrap border-r border-border px-3 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.length === 0 && (
                        <tr><td colSpan={Math.max(data.columns.length, 1)} className="py-8 text-center text-muted-foreground">No items — sync this source first.</td></tr>
                      )}
                      {data.rows.map((r, i) => (
                        <tr key={i} className="border-b border-border hover:bg-muted/40">
                          {data.columns.map((c) => (
                            <td key={c} className="max-w-[280px] truncate whitespace-nowrap border-r border-border px-3 py-1.5 font-mono text-[11px]" title={Array.isArray(r[c]) ? r[c].join(' | ') : r[c]}>
                              {Array.isArray(r[c]) ? r[c].join(' | ') : r[c]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </>
      )}
    </Dialog>
  );
}

// Group a source's fields by category (after applying the search query) so the
// Shopify metafields / images / collections / stock keys are easy to find.
function groupFields(fields, q) {
  const ql = (q || '').toLowerCase();
  const filtered = ql ? fields.filter((f) => f.name.toLowerCase().includes(ql)) : fields;
  const defs = [
    ['Metafields', (n) => n.startsWith('meta_')],
    ['Images', (n) => n === 'image_link' || n === 'additional_image_link'],
    ['Collections', (n) => n === 'collections'],
    ['Stock & inventory', (n) => ['availability', 'inventory_quantity', 'inventory_policy', 'status'].includes(n)],
    ['Pricing & identifiers', (n) => ['price', 'compare_at_price', 'sku', 'gtin', 'id', 'product_id', 'handle'].includes(n)],
    ['Variant options', (n) => n.startsWith('option_')],
  ];
  const groups = defs.map(([label, test]) => ({ label, test, fields: [] }));
  const other = { label: 'Other / core', fields: [] };
  filtered.forEach((f) => { (groups.find((g) => g.test(f.name)) || other).fields.push(f); });
  return [...groups, other].filter((g) => g.fields.length).map(({ label, fields: ff }) => ({ label, fields: ff }));
}

function StageLabel({ n, children }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className="font-mono text-[10px] text-primary">{n}</span>
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{children}</span>
    </div>
  );
}

function MetaRow({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[11px] text-muted-foreground">{k}</span>
      <span className="tnum text-sm font-medium">{v}</span>
    </div>
  );
}

function Arrow() {
  return <div className="flex shrink-0 items-center px-1 text-muted-foreground"><ArrowRight className="h-5 w-5" /></div>;
}

function SourceNode({ s, step, count, onClick }) {
  const enrich = s.role === 'enrichment';
  const tag = enrich ? 'attach' : step === 1 ? 'start' : 'base';
  return (
    <button
      onClick={() => onClick(s)}
      title="Open preview"
      className={cn(
        'group w-full rounded-lg border bg-card px-3.5 py-2.5 text-left shadow-xs transition-all hover:-translate-y-px hover:shadow-card',
        enrich ? 'border-dashed border-border' : 'border-border',
        step === 1 && 'ring-1 ring-primary/40'
      )}
    >
      <div className="flex items-center gap-2">
        <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/20 font-mono text-[10px] font-semibold text-foreground">{step}</span>
        <span className="flex-1 truncate text-sm font-medium">{s.name}</span>
        <Badge variant={enrich ? 'default' : 'accent'}>{tag}</Badge>
      </div>
      <div className="mt-1 flex items-center justify-between font-mono text-[11px] text-muted-foreground">
        <span>{s.type}</span>
        <span className="inline-flex items-center gap-1">
          {count.live && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" title="live" />}
          {count.n == null ? (count.loading ? '…' : '—') : `${fmtNum(count.n)} rows`}
        </span>
      </div>
      <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">join · {s.config?.id_field || 'id'}</div>
    </button>
  );
}

function SourcesDiagram({ sources, feeds, fieldCount, liveCounts, onNodeClick, onFeeds }) {
  const countFor = (s) => {
    if (s.type === 'postgres') {
      const lc = liveCounts?.[s.id];
      if (lc?.ok) return { n: lc.count, live: true };
      if (lc && !lc.ok) return { n: null };
      return { n: null, loading: true };
    }
    return { n: s.item_count || 0 };
  };
  const master = sources[0];
  const attachments = sources.slice(1);
  const masterCount = master ? countFor(master) : { n: 0 };
  const products = masterCount.n || 0;
  const showN = (c) => (c.n == null ? (c.loading ? '…' : '—') : fmtNum(c.n));

  const ColHead = ({ children }) => (
    <p className="mb-2 h-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{children}</p>
  );

  return (
    <Card className="overflow-x-auto p-6">
      <div className="flex min-w-[980px] items-stretch gap-3">
        {/* 1 · Master */}
        <div className="flex w-[250px] shrink-0 flex-col justify-center">
          <ColHead>Master</ColHead>
          {master && (
            <button onClick={() => onNodeClick(master)} className="rounded-lg border-2 border-primary bg-primary/5 p-4 text-left shadow-glow transition-all hover:-translate-y-px">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">product master</span>
                <Database className="h-4 w-4" />
              </div>
              <p className="mt-1 truncate text-sm font-semibold">{master.name}</p>
              <p className="tnum mt-2 text-3xl font-semibold">{showN(masterCount)}</p>
              <p className="font-mono text-[11px] text-muted-foreground">
                {masterCount.live && <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 align-middle" />}
                product ids · key {master.config?.id_field || 'id'}
              </p>
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center"><ArrowRight className="h-5 w-5 text-muted-foreground" /></div>

        {/* 2 · Sources (attachments) */}
        <div className="flex w-[300px] shrink-0 flex-col">
          <ColHead>Sources · merged by id</ColHead>
          <div className="max-h-[62vh] space-y-2 overflow-y-auto pr-1">
            {attachments.map((s, i) => <SourceNode key={s.id} s={s} step={i + 2} count={countFor(s)} onClick={onNodeClick} />)}
            {attachments.length === 0 && <p className="text-sm text-muted-foreground">No other sources yet.</p>}
          </div>
        </div>

        <div className="flex shrink-0 items-center"><ArrowRight className="h-5 w-5 text-muted-foreground" /></div>

        {/* 3 · Unified catalog */}
        <div className="flex w-[210px] shrink-0 flex-col justify-center">
          <ColHead>Unified catalog</ColHead>
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 shadow-glow">
            <div className="flex items-center gap-2"><Database className="h-4 w-4" /><span className="font-semibold">Unified catalog</span></div>
            <p className="mt-1 font-mono text-[11px] text-muted-foreground">one row per product</p>
            <div className="mt-3 space-y-1.5">
              <MetaRow k="products" v={fmtNum(products)} />
              <MetaRow k="fields" v={fieldCount ?? '—'} />
              <MetaRow k="sources" v={sources.length} />
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center"><ArrowRight className="h-5 w-5 text-muted-foreground" /></div>

        {/* 4 · Feeds */}
        <div className="flex w-[160px] shrink-0 flex-col justify-center">
          <ColHead>Outputs</ColHead>
          <button onClick={onFeeds} className="rounded-lg border border-border bg-card p-4 text-left shadow-xs transition-all hover:-translate-y-px hover:shadow-card">
            <div className="flex items-center gap-2"><Rss className="h-4 w-4" /><span className="font-semibold">Feeds</span></div>
            <p className="tnum mt-2 text-3xl font-semibold">{feeds.length}</p>
            <p className="font-mono text-[11px] text-muted-foreground">outputs</p>
          </button>
        </div>
      </div>

      <p className="mt-5 text-xs text-muted-foreground">
        <strong>{master?.name || 'The product master'}</strong> is the spine — every other source merges its columns onto it by the join id, forming one unified catalog the feeds are built from. Click any node to preview its fields &amp; data.
      </p>
    </Card>
  );
}
